<script setup lang="ts">
import { computed } from 'vue';
import { useTrip } from '@/stores/trip';
import { STATIONS, STATION_INDEX } from '@/data/graph';
import { fmtDate, fmtDateShort, fmtHour, fmtTimeCompact } from '@/core/format';
import { CO2_CAR, CO2_PLANE } from '@/core/constants';
import ModeCompare from './ModeCompare.vue';

/**
 * Récapitulatif détaillé du voyage en plusieurs cartes :
 *   1. Calendrier (départ / retour / durée totale) — si une date est fixée
 *   2. Détail des trajets (répartition train/bus en barre, correspondances)
 *   3. Empreinte CO₂ avec comparaison voiture/avion
 *   4. Programme jour par jour — si une date est fixée
 *   5. Comparaison de modes (composant à part)
 *
 * N'affiche rien tant qu'il y a moins de 2 étapes.
 */
const trip = useTrip();

const calendar = computed(() => trip.calendar.value);
const totals = computed(() => calendar.value.totals);

const totalDays = computed(() => {
  const c = calendar.value;
  if (!c.startDate || !c.endDate) return 0;
  return Math.ceil(
    (c.endDate.getTime() - c.startDate.getTime()) / (1000 * 60 * 60 * 24),
  );
});

// Répartition pourcentage train / bus / nuit pour la barre
const transportBar = computed(() => {
  const total = totals.value.trainMin + totals.value.busMin;
  if (total <= 0) return null;
  return {
    trainPct: (totals.value.trainMin / total) * 100,
    busPct: (totals.value.busMin / total) * 100,
    nightPct: (totals.value.nightTrainMin / total) * 100,
  };
});

const co2Comparison = computed(() => {
  if (totals.value.unreachable || totals.value.totalCO2 <= 0) return null;
  const kg = totals.value.totalCO2 / 1000;
  const carEq = (totals.value.totalKm * CO2_CAR) / 1000;
  const planeEq = (totals.value.totalKm * CO2_PLANE) / 1000;
  const savedVsCar = carEq - kg;
  return {
    kg,
    carEq,
    planeEq,
    savedVsCar,
    display:
      kg < 1
        ? `${Math.round(totals.value.totalCO2)} g`
        : `${kg.toFixed(1)} kg`,
  };
});

/** Programme jour par jour, pré-calculé pour le template */
const programItems = computed(() => {
  const rows: Array<{
    date: string;
    text: string;
    sub?: string;
    isStrong?: boolean;
  }> = [];
  if (!calendar.value.hasDate) return rows;

  const stops = trip.state.routeStops;
  stops.forEach((stop, i) => {
    const s = STATIONS[STATION_INDEX[stop.id]];
    const calItem = calendar.value.cal[i];
    const isFirst = i === 0;
    const isLast = i === stops.length - 1;

    if (calItem.arrive) {
      rows.push({
        date: fmtDateShort(calItem.arrive),
        text: isFirst ? `Départ depuis ${s.name}` : `Arrivée à ${s.name}`,
        sub: `à ${fmtHour(calItem.arrive)}`,
        isStrong: true,
      });
    }
    if (stop.nights > 0 && !isLast) {
      rows.push({
        date: '',
        text: '',
        sub: `${stop.nights} ${stop.nights > 1 ? 'nuits sur place' : 'nuit sur place'}`,
      });
    }
    if (!isLast && calItem.depart) {
      const nextS = STATIONS[STATION_INDEX[stops[i + 1].id]];
      const tripDuration = calItem.segmentPath
        ? ` · ${fmtTimeCompact(calItem.segmentPath.total)} de trajet`
        : '';
      rows.push({
        date: fmtDateShort(calItem.depart),
        text: `Départ vers ${nextS.name}`,
        sub: `à ${fmtHour(calItem.depart)}${tripDuration}`,
        isStrong: true,
      });
    }
  });
  return rows;
});

const showSummary = computed(() => trip.state.routeStops.length >= 2);
</script>

<template>
  <div v-if="showSummary" class="space-y-3">
    <!-- Calendrier -->
    <section
      v-if="calendar.hasDate && calendar.startDate && calendar.endDate && !totals.unreachable"
      class="p-4 bg-surface border border-DEFAULT rounded-md"
    >
      <h3 class="flex items-center gap-2 text-sm font-medium mb-3">
        <i class="ti ti-calendar text-accent" aria-hidden="true"></i>
        Calendrier du voyage
      </h3>
      <div class="space-y-2 text-sm">
        <div class="flex justify-between">
          <span class="text-text-muted">Départ</span>
          <span class="font-medium text-accent">
            {{ fmtDate(calendar.startDate) }} · {{ fmtHour(calendar.startDate) }}
          </span>
        </div>
        <div class="flex justify-between">
          <span class="text-text-muted">Retour</span>
          <span class="font-medium text-accent">
            {{ fmtDate(calendar.endDate) }} · {{ fmtHour(calendar.endDate) }}
          </span>
        </div>
        <div class="flex justify-between">
          <span class="text-text-muted">Durée totale</span>
          <span class="font-medium">
            {{ totalDays }} {{ totalDays > 1 ? 'jours' : 'jour' }}
          </span>
        </div>
      </div>
    </section>

    <!-- Détail des trajets -->
    <section
      v-if="!totals.unreachable && totals.totalMin > 0"
      class="p-4 bg-surface border border-DEFAULT rounded-md"
    >
      <h3 class="flex items-center gap-2 text-sm font-medium mb-3">
        <i class="ti ti-train text-accent" aria-hidden="true"></i>
        Détail des trajets
      </h3>

      <div v-if="transportBar" class="space-y-3">
        <!-- Barre proportionnelle -->
        <div class="flex h-2 rounded-full overflow-hidden bg-surface-muted">
          <template v-if="totals.nightTrainMin > 0">
            <div
              class="bg-mode-night"
              :style="{ width: `${transportBar.nightPct}%` }"
            ></div>
            <div
              class="bg-mode-train"
              :style="{ width: `${transportBar.trainPct - transportBar.nightPct}%` }"
            ></div>
          </template>
          <div
            v-else
            class="bg-mode-train"
            :style="{ width: `${transportBar.trainPct}%` }"
          ></div>
          <div
            class="bg-mode-bus"
            :style="{ width: `${transportBar.busPct}%` }"
          ></div>
        </div>

        <!-- Chips -->
        <div class="flex flex-wrap gap-2 text-xs">
          <span
            v-if="totals.trainMin > 0"
            class="inline-flex items-center gap-1 px-2 py-1 bg-mode-train/10 text-mode-train rounded-full"
          >
            <i class="ti ti-train" aria-hidden="true"></i>
            Train {{ fmtTimeCompact(totals.trainMin) }}
          </span>
          <span
            v-if="totals.nightTrainMin > 0"
            class="inline-flex items-center gap-1 px-2 py-1 bg-mode-night/10 text-mode-night rounded-full"
          >
            <i class="ti ti-moon" aria-hidden="true"></i>
            Dont nuit {{ fmtTimeCompact(totals.nightTrainMin) }}
          </span>
          <span
            v-if="totals.busMin > 0"
            class="inline-flex items-center gap-1 px-2 py-1 bg-mode-bus/10 text-mode-bus rounded-full"
          >
            <i class="ti ti-bus" aria-hidden="true"></i>
            Bus {{ fmtTimeCompact(totals.busMin) }}
          </span>
        </div>
      </div>

      <div class="mt-3 space-y-1.5 text-sm">
        <div class="flex justify-between">
          <span class="text-text-muted">Trajets directs</span>
          <span class="font-medium">{{ totals.totalDirects }} / {{ totals.totalSegs }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-text-muted">Correspondances</span>
          <span class="font-medium">{{ totals.totalSegs - totals.totalDirects }}</span>
        </div>
        <div v-if="totals.nightsSavedByTrain > 0" class="flex justify-between">
          <span class="text-text-muted">Nuits gagnées en train</span>
          <span class="font-medium text-green-700 inline-flex items-center gap-1">
            <i class="ti ti-moon text-xs" aria-hidden="true"></i>
            {{ totals.nightsSavedByTrain }}
          </span>
        </div>
      </div>
    </section>

    <!-- CO2 -->
    <section
      v-if="co2Comparison"
      class="p-4 bg-surface border border-DEFAULT rounded-md"
    >
      <h3 class="flex items-center gap-2 text-sm font-medium mb-3">
        <i class="ti ti-leaf text-accent" aria-hidden="true"></i>
        Empreinte CO₂
      </h3>
      <div class="space-y-1.5 text-sm">
        <div class="flex justify-between">
          <span class="text-text-muted">Voyage train + bus</span>
          <span class="font-medium text-green-700">{{ co2Comparison.display }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-text-muted">vs. voiture seule</span>
          <span class="font-medium">{{ co2Comparison.carEq.toFixed(1) }} kg</span>
        </div>
        <div class="flex justify-between">
          <span class="text-text-muted">vs. avion direct</span>
          <span class="font-medium">{{ co2Comparison.planeEq.toFixed(1) }} kg</span>
        </div>
        <div
          v-if="co2Comparison.savedVsCar > 0"
          class="flex justify-between pt-2 mt-2 border-t border-DEFAULT"
        >
          <span class="text-text-muted">Économie vs voiture</span>
          <span class="font-medium text-green-700">
            −{{ co2Comparison.savedVsCar.toFixed(1) }} kg CO₂
          </span>
        </div>
      </div>
    </section>

    <!-- Programme jour par jour -->
    <section
      v-if="calendar.hasDate && programItems.length > 0"
      class="p-4 bg-surface border border-DEFAULT rounded-md"
    >
      <h3 class="flex items-center gap-2 text-sm font-medium mb-3">
        <i class="ti ti-list-details text-accent" aria-hidden="true"></i>
        Programme jour par jour
      </h3>
      <ol class="space-y-2 text-sm">
        <li
          v-for="(row, i) in programItems"
          :key="i"
          class="flex gap-3"
        >
          <span class="font-mono text-xs text-text-faint min-w-[42px] pt-0.5">
            {{ row.date }}
          </span>
          <span class="flex-1">
            <strong v-if="row.text && row.isStrong">{{ row.text }}</strong>
            <span v-else-if="row.text">{{ row.text }}</span>
            <span v-if="row.sub" class="text-text-muted">
              {{ row.text ? ' ' : '' }}{{ row.sub }}
            </span>
          </span>
        </li>
      </ol>
    </section>

    <!-- Compare -->
    <ModeCompare />
  </div>
</template>
